import { ArrayField, Datagrid, FunctionField, ReferenceField, TextField, useNotify } from "react-admin";
import { Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { settings } from "app";
import { getFile } from "server/components/DownloadFile";
import { CampaignTypes } from "utils/constants";
import { administrationRequests } from "api/administrationApi";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import dataProvider from "server/dataProvider";
import EditPreferenceAdditionalDataDialog from "./EditPreferenceAdditionalDataDialog";
import { PreferenceOrigin } from "utils/constants"

const CandidacyPreferences = ({ record, isForeigner, handleKey }) => {
  const notify = useNotify();

  const [preferenceType, setPreferenceType] = useState(null);
  // Map preferenceId -> [{ key, labelBg }] of preferences that support additional data.
  const [additionalDataFields, setAdditionalDataFields] = useState({});

  useEffect(() => {
    const fetchPreferenceTypes = async () => {
      try {
        const { data } = await dataProvider.customRequest(administrationRequests.getPreferenceDocumentTypesSelect());
        setPreferenceType(data);
      } catch (err) {
        console.error("Неуспешно зареждане на типовете документи", err);
        setPreferenceType([]);
      }
    };

    const fetchAdditionalDataFields = async preferenceIds => {
      if (!preferenceIds.length) return;
      try {
        const { data } = await dataProvider.customRequest(
          administrationRequests.getPreferencesAdditionalData(preferenceIds),
        );
        const fieldsByPreference = (data ?? []).reduce((acc, { preferenceId, key, labelBg }) => {
          (acc[preferenceId] ??= []).push({ key, labelBg });
          return acc;
        }, {});
        setAdditionalDataFields(fieldsByPreference);
      } catch (err) {
        console.error("Неуспешно зареждане на допълнителните данни за преференции", err);
        setAdditionalDataFields({});
      }
    };

    fetchPreferenceTypes();
    fetchAdditionalDataFields((record?.preferences ?? []).map(pref => pref.preferenceId).filter(Boolean));
  }, [record?.preferences]);

  if (
    isForeigner ||
    !(record?.preferences?.length > 0) ||
    (handleKey === PreferenceOrigin.Candidacy && record?.campaignTypeId === CampaignTypes.PreparatoryCourses)
  ) {
    return null;
  }

  const permissionsMap = {
    student: permissions.canUpdateStudentPreferenceAdditionalData,
    candidacy: permissions.canUpdateCandidacyPreferenceAdditionalData,
  };

  return (
    <>
      {handleKey !== PreferenceOrigin.Student && (
        <Typography
          fontWeight={600}
          sx={{ color: "#4E2683" }}
        >
          Избрани преференции
        </Typography>
      )}

      <ArrayField source="preferences">
        <Datagrid
          rowClick={false}
          bulkActionButtons={false}
          sx={{ ...settings.style.secondaryDatagrid, mb: 3 }}
        >
          <ReferenceField
            source="preferenceId"
            reference={administrationRequests.getPreferencesSelect()}
            label="Преференция"
          >
            <TextField source="nameBg" />
          </ReferenceField>
          <FunctionField
            label="Допълнителни данни"
            render={pref => {
              const definitions = additionalDataFields[pref?.preferenceId];
              // Prefer the fetched labels, but fall back to the raw stored data (keyed by its
              // own key) so existing values stay visible even if the definitions request failed.
              const rows = (
                definitions?.length > 0
                  ? definitions.map(field => ({ key: field.key, label: field.labelBg }))
                  : Object.keys(pref?.additionalData ?? {}).map(key => ({ key, label: key }))
              )
                .map(({ key, label }) => ({ key, label, value: pref?.additionalData?.[key] }))
                .filter(row => row.value);
              return rows.length > 0
                ? rows.map(row => (
                    <Typography
                      sx={{ fontSize: "14px" }}
                      key={row.key}
                    >
                      {row.label}: {row.value}
                    </Typography>
                  ))
                : "-";
            }}
          />
          <FunctionField
            label="Тип документ"
            render={record =>
              record.documentTypes.map((doc, idx) => (
                <Typography
                  sx={{ fontSize: "14px" }}
                  key={idx}
                >
                  {preferenceType?.find(x => x.id === doc.documentTypeId)?.name}
                </Typography>
              ))
            }
          />
          <FunctionField
            label={"Свали документ"}
            render={record =>
              record.documentTypes.map((doc, idx) =>
                doc.documentIds.map((fileId, fileidx) =>
                  fileId > 0 ? (
                    <Typography
                      key={fileId}
                      sx={{ textDecoration: "underline", fontSize: "14px", cursor: "pointer" }}
                      onClick={() => getFile(fileId, notify)}
                    >{`file-${fileidx}.pdf`}</Typography>
                  ) : null,
                ),
              )
            }
          />
          <SecuredComponent permission={permissionsMap[handleKey]}>
            <FunctionField
              label="Действия"
              render={pref => {
                const fields = additionalDataFields[pref?.preferenceId];
                // Only preferences that support additional data get the action.
                return fields?.length > 0 ? (
                  <EditPreferenceAdditionalDataDialog
                    mainId={handleKey === PreferenceOrigin.Student ? record?.studentId : record?.id}
                    preference={pref}
                    fields={fields}
                    disabled={record?.hasApprovedRanking}
                    handleKey={handleKey}
                  />
                ) : null;
              }}
            />
          </SecuredComponent>
        </Datagrid>
      </ArrayField>
    </>
  );
};

export default CandidacyPreferences;
